import { useState, useEffect } from 'react';
import { Plus, Eye, Check, X, Archive, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const Projects = () => {
  const { isDark } = useTheme();
  const [filter, setFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Map API projects to match the expected format
  const mappedProjects = projects.map(project => ({
    id: project.id,
    name: project.name,
    tagline: project.tagline,
    category: project.category,
    status: project.status,
    leader: project.team?.[0]?.name || 'N/A',
    teamSize: project.team?.length || 0,
    applications: 0, // Will be fetched separately if needed
    approvalStatus: project.is_featured ? 'Approved' : 'Pending',
    createdAt: project.created_at ? new Date(project.created_at).toLocaleDateString('en-GB') : 'N/A',
    isArchived: false,
  }));

  const filteredProjects =
    filter === 'all'
      ? mappedProjects
      : filter === 'pending'
      ? mappedProjects.filter(p => p.approvalStatus === 'Pending')
      : filter === 'archived'
      ? mappedProjects.filter(p => p.isArchived)
      : mappedProjects.filter(p => !p.isArchived);

  const handleApprove = async (id) => {
    try {
      // TODO: Implement approval endpoint in backend
      await axios.patch(`${API}/projects/${id}`, { is_featured: true });
      fetchProjects(); // Refresh data
    } catch (error) {
      console.error('Error approving project:', error);
    }
  };

  const handleReject = async (id) => {
    try {
      // TODO: Implement rejection endpoint in backend
      await axios.patch(`${API}/projects/${id}`, { is_featured: false });
      fetchProjects(); // Refresh data
    } catch (error) {
      console.error('Error rejecting project:', error);
    }
  };

  const getStatusColor = (status) => {
    if (isDark) {
      switch (status) {
        case 'Live':
          return 'bg-green-500/30 text-green-200';
        case 'Building':
          return 'bg-pink-500/30 text-pink-200';
        case 'Testing':
          return 'bg-blue-500/30 text-blue-200';
        case 'Ideation':
          return 'bg-yellow-500/30 text-yellow-200';
        default:
          return 'bg-gray-500/30 text-gray-300';
      }
    } else {
      switch (status) {
        case 'Live':
          return 'bg-green-100 text-green-700';
        case 'Building':
          return 'bg-pink-100 text-pink-700';
        case 'Testing':
          return 'bg-blue-100 text-blue-700';
        case 'Ideation':
          return 'bg-yellow-100 text-yellow-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
        <div>
          <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Projects</h2>
          <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Manage projects, approvals, and archive</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
          <Button className="w-full sm:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm sm:text-base">
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </Button>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className={`w-full sm:w-40 ${
              isDark 
                ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/30 text-purple-200' 
                : 'bg-white border-gray-300'
            }`}>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/30' : ''}>
              <SelectItem value="all" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>All Projects</SelectItem>
              <SelectItem value="pending" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Pending Approval</SelectItem>
              <SelectItem value="active" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Active</SelectItem>
              <SelectItem value="archived" className={isDark ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white focus:bg-purple-500/20 focus:text-white' : ''}>Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Projects</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-white' : 'text-gray-900'}`}>{mappedProjects.length}</p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pending Approval</p>
          <p className="text-2xl font-bold text-orange-600">
            {mappedProjects.filter(p => p.approvalStatus === 'Pending').length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Active Projects</p>
          <p className="text-2xl font-bold text-green-600">
            {mappedProjects.filter(p => p.approvalStatus === 'Approved' && !p.isArchived).length}
          </p>
        </div>
        <div className={`rounded-lg p-4 border transition-colors shadow-sm ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={`text-sm transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Archived</p>
          <p className={`text-2xl font-bold transition-colors ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {mappedProjects.filter(p => p.isArchived).length}
          </p>
        </div>
      </div>

      {/* Projects - Desktop Table / Mobile Cards */}
      {loading ? (
        <div className={`rounded-xl shadow-md border p-8 sm:p-12 text-center transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading projects...</p>
        </div>
      ) : (
        <div className={`rounded-xl shadow-md border overflow-hidden transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-50'}>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ID</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>PROJECT NAME</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>CATEGORY</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>STATUS</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>LEADER</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>TEAM SIZE</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>APPLICATIONS</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>APPROVAL</TableHead>
                  <TableHead className={`font-semibold transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((project) => (
                  <tr
                    key={project.id}
                    className={`border-b transition-colors group ${
                      isDark 
                        ? 'border-purple-500/20 hover:bg-purple-500/10' 
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <TableCell className={`font-medium transition-colors ${
                      isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                    }`}>{project.id}</TableCell>
                    <TableCell>
                      <div>
                        <p className={`font-medium transition-colors ${
                          isDark ? 'text-gray-200 group-hover:text-white' : 'text-gray-900'
                        }`}>{project.name}</p>
                        <p className={`text-xs transition-colors ${
                          isDark ? 'text-gray-400 group-hover:text-gray-300' : 'text-gray-500'
                        }`}>{project.tagline}</p>
                      </div>
                    </TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{project.category}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{project.leader}</TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{project.teamSize}</TableCell>
                    <TableCell className={isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700'}>{project.applications}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          project.approvalStatus === 'Approved'
                            ? isDark
                              ? 'bg-green-500/30 text-green-200'
                              : 'bg-green-100 text-green-700'
                            : isDark
                              ? 'bg-orange-500/30 text-orange-200'
                              : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {project.approvalStatus}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                          isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                        }`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        {project.approvalStatus === 'Pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-8 w-8 p-0 ${
                                isDark 
                                  ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                                  : 'text-green-600'
                              }`}
                              onClick={() => handleApprove(project.id)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-8 w-8 p-0 ${
                                isDark 
                                  ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                                  : 'text-red-600'
                              }`}
                              onClick={() => handleReject(project.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {project.isArchived ? (
                          <Button variant="ghost" size="sm" className={`h-8 px-3 ${
                            isDark ? 'text-purple-200 hover:text-white hover:bg-purple-500/20' : ''
                          }`}>
                            Unarchive
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" className={`h-8 w-8 p-0 ${
                            isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                          }`}>
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </tr>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-purple-500/20">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className={`p-4 transition-colors ${
                  isDark 
                    ? 'hover:bg-purple-500/10' 
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-semibold transition-colors ${
                        isDark ? 'text-purple-300' : 'text-purple-600'
                      }`}>{project.id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          project.approvalStatus === 'Approved'
                            ? isDark
                              ? 'bg-green-500/30 text-green-200'
                              : 'bg-green-100 text-green-700'
                            : isDark
                              ? 'bg-orange-500/30 text-orange-200'
                              : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {project.approvalStatus}
                      </span>
                    </div>
                    <h3 className={`font-semibold mb-1 transition-colors ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{project.name}</h3>
                    <p className={`text-xs mb-2 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>{project.tagline}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                    <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                      isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                    }`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                    {project.approvalStatus === 'Pending' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${
                            isDark 
                              ? 'text-green-400 hover:text-green-300 hover:bg-green-500/20' 
                              : 'text-green-600'
                          }`}
                          onClick={() => handleApprove(project.id)}
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${
                            isDark 
                              ? 'text-red-400 hover:text-red-300 hover:bg-red-500/20' 
                              : 'text-red-600'
                          }`}
                          onClick={() => handleReject(project.id)}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                    {!project.isArchived && (
                      <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${
                        isDark ? 'text-gray-300 hover:text-white hover:bg-purple-500/20' : ''
                      }`}>
                        <Archive className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Category</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{project.category}</p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Leader</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{project.leader}</p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Team Size</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{project.teamSize}</p>
                  </div>
                  <div>
                    <p className={`text-xs mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Applications</p>
                    <p className={`font-medium transition-colors ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>{project.applications}</p>
                  </div>
                </div>
                {project.isArchived && (
                  <div className="mt-3 pt-3 border-t border-purple-500/20">
                    <Button variant="ghost" size="sm" className={`w-full justify-start ${
                      isDark 
                        ? 'text-purple-200 hover:bg-purple-500/20 hover:text-white' 
                        : 'text-purple-700 hover:bg-purple-50'
                    }`}>
                      Unarchive Project
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;

